import { useSyncExternalStore } from "react";
import { isAdminActingNow } from "@/lib/adminActing";

// adminWriteGate — the confirm-before-every-write gate for an admin acting as a store.
//
// The nine merchant write call sites live across five components, so the gate sits at the API
// SERVICE layer that every write already funnels through, not at each call site. No toast
// interception, no monkey-patching, no call-site edits.
//
// A declined confirmation returns a plain { ok: false, error: ADMIN_WRITE_CANCELLED } from the
// service. Every call site already ends in `if (!res.ok) { toast.error(res.error); return; }`
// and resets its loading flag on the line ABOVE that check, so the cancel path unwinds cleanly
// with nothing stranded and the user sees a calm Arabic notice, not a server-error string.
// Returning { ok: true } instead would fall through to post-success navigation and crash;
// throwing would strand the loading-flag resets. Neither hazard is reachable this way.
//
// When the user is NOT acting, confirmAdminWrite resolves true immediately with zero UI, so a
// real merchant sees no behavioural change whatsoever.
//
// The dialog host that answers these promises is AdminWriteConfirmDialog (JSX, so it lives in
// components/), mounted unconditionally by MerchantLayout.

type PendingWrite = {
  label: string;
  resolve: (ok: boolean) => void;
};

let pending: PendingWrite | null = null;
let listeners: Array<() => void> = [];

const notify = () => listeners.forEach((l) => l());

const subscribe = (listener: () => void) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
};

const getSnapshot = (): string | null => (pending ? pending.label : null);

export const ADMIN_WRITE_CANCELLED = "تم إلغاء العملية";

export const confirmAdminWrite = (label: string): Promise<boolean> => {
  if (!isAdminActingNow()) return Promise.resolve(true);
  // One confirmation at a time. A second concurrent write is declined rather than queued, so
  // no dialog can ever be answered for the wrong operation.
  if (pending) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    pending = { label, resolve };
    notify();
  });
};

export const resolveAdminWrite = (ok: boolean): void => {
  const current = pending;
  pending = null;
  notify();
  current?.resolve(ok);
};

export const usePendingAdminWrite = (): string | null =>
  useSyncExternalStore(subscribe, getSnapshot, () => null);
